import {Injectable} from '@angular/core';
import {
  arrayRemove,
  arrayUnion,
  collection,
  collectionChanges,
  deleteField,
  doc,
  docData,
  documentId,
  DocumentReference,
  DocumentSnapshot,
  FieldPath,
  Firestore,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  QueryDocumentSnapshot,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from '@angular/fire/firestore';
import {getDownloadURL, ref, Storage} from '@angular/fire/storage';
import {BehaviorSubject, EMPTY, forkJoin, from, Observable, of, Subscriber, Subscription} from 'rxjs';
import {FireAuthService} from './fire-auth.service';
import {filter, map, mergeMap, startWith, take} from 'rxjs/operators';
import {NavigationEnd, Router} from '@angular/router';

import {
  ApiLogObj2,
  Colleague,
  editableKeys,
  Feature,
  Message,
  PriceBand,
  keyToTitle,
  sItemKeyToInt,
  StockItem,
  StockItemUpdateObj,
  StockValChangeCheckConfig,
  StockValuesChangeCheckResult,
  StoreInfo,
  Supplier,
} from '../models-old/datastructures';
import {AccessFlags, pageRules} from '../models-old/utils-old/rule-structure';
import {AutoOrder} from '../../features/auto-ordering/models/old-models/ao-datastructures';
import {StockFunctions} from '../functions-old/stock-functions';
import {
  StockValChangeFlagsComponent,
} from '../../shared-modules/shared-module/components/stock-val-change-flags/stock-val-change-flags.component';
import {IStore} from '../../shared/shared-models/store/store';
import {StoreObject} from '../models-old/store/store-object';
import {IPriceBand, IPriceBandPerStore} from '../../shared/shared-models/price-banding/price-band';
import {getDeepCopyOfObject} from '../../shared/shared-utils/object/object.utils';
import {IDepartment, ISubDepartment} from '../../shared/shared-models/stock/departments';
import {IDefaultGroupAccessDocument, IGroupAccess, IUser, IUserAccess} from '../../features/user-access/models';
import {RuleHumanID} from '../../features/user-access/models/rule-info.enum';
import {
  CollectionStoresSettingsService,
} from '../../shared/shared-services/firebase/collection-stores-settings.service';
import {
  path_feature_stores_data_storeId_singular_documents_document,
  path_operational_stores_data_auto_ordering_auto_orders_GET,
  path_operational_stores_data_storeId_messages_from_app,
  path_stores,
  path_stores_storeId_settings_price_bands,
  path_stores_storeId_settings_stock_edit_thresholds,
  path_user_access_userId,
  path_users_userId,
  path_users_userId_page_alerts_url,
  path_users_userId_settings_saved_preferences_visible_Fields_feature,
  path_users_userId_singular_documents_document,
} from '../../shared/shared-services/database-paths';
import {
  PATH_AUTO_ORDER_ORDER_ITEMS_BY_STORE_ID_AND_ORDER_ID_AT_ITEM_CODE,
} from '../../shared/shared-services/database-paths.operational';
import {ISupplier} from 'src/app/shared/shared-models/stock/suppliers';
import {ISharedModalBasic, ISharedModalBasicResponse} from '../../shared/shared-models/modals/shared-modal-basic';
import {IconColourEnum} from '../../shared-modules/shared-icons/models/icons-colour.enum';
import {
  SharedModalBasicComponent,
} from '../../shared/shared-components/components/shared-modal-basic/shared-modal-basic.component';
import {
  IAutoOrderFirestoreOrderPreparedInfo,
  IAutoOrderFirestoreOrderPreparedInfoDetails,
  IAutoOrderOrderItem,
  TAutoOrderRecords,
} from '../../features/auto-ordering/models';
import {IconsEnum} from '../../shared-modules/shared-icons/models/icons.enum';
import {IModalDismissResult, ModalControllerService} from '../../shared/shared-services/overlay/modal-controller.service';
import {encodeStrict} from 'src/app/shared/shared-utils/firestore/firestore.utils';
import {IGlobalError} from '../../shared/shared-components/components/global-error-display/models/global-error';
import {IAutoOrderAccountSettings} from '../../features/settings-new/models';
import {normalizeAutoOrderUnscheduledPayload} from '../../features/auto-ordering/utils/ao-account-settings.utils';
import {createGlobalPaths} from '../../shared/shared-services/database-paths.global';
import {createOperationalServerTimestamp} from '../../shared/shared-utils/firestore/operational-server-timestamp.utils';

export const DOCUMENT_ID = documentId;
export const DELETE_FILED = deleteField;
export const SERVER_TIMESTAMP = serverTimestamp;
export const ARRAY_UNION = arrayUnion;
export const ARRAY_REMOVE = arrayRemove;

interface IScannerAutoOrderRequest extends IAutoOrderAccountSettings {
  preparedItems: Record<string, IAutoOrderFirestoreOrderPreparedInfoDetails>;
  total: number;
  userId: string;
  weeksHistory: number;
  comment?: string;
}

@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  private userId = 'This has not been set.';
  private userAccess: IUserAccess = {} as IUserAccess;
  private sVChangeCheckConfigs: Record<string, StockValChangeCheckConfig> = {};

  private readonly userObjSubject: BehaviorSubject<IUser>;

  // Todo: Convert this to IStore
  private readonly storesMapObs: BehaviorSubject<{ stores: Record<string, StoreInfo>; order: string[] }>;

  private readonly colleaguesBS: BehaviorSubject<{
    users: Record<string, Colleague>;
    storesUsers: Record<string, string[]>;
  }>;

  constructor(
    private firestore: Firestore,
    private storage: Storage,
    private fireAuthService: FireAuthService,
    private router: Router,
    private modalController: ModalControllerService,
    private collectionStoresSettingsService: CollectionStoresSettingsService,
    private collectionGlobalService: CollectionStoresSettingsService,
  ) {
    let userAccessSub: Subscription;
    this.storesMapObs = new BehaviorSubject<
      { stores: Record<string, StoreInfo>; order: string[] }
    >({stores: {}, order: []});
    this.colleaguesBS = new BehaviorSubject({users: {}, storesUsers: {}});

    this.userObjSubject = new BehaviorSubject<IUser>(null);

    this.fireAuthService.userIdSub.subscribe(async (userId: string) => {

      if (userId) {

        if (this.userId && this.userId !== userId && userAccessSub) {
          userAccessSub.unsubscribe();
        }

        if (this.userId !== userId) {

          this.userId = userId;

          userAccessSub = docData(doc(this.firestore, path_user_access_userId(userId))).subscribe(data => {
            this.userAccess = data as IUserAccess;

            if (this.userAccess?.storeList && this.userAccess?.storeList.length > 0) {
              const storesQuery = query(
                collection(this.firestore, path_stores()),
                where(DOCUMENT_ID(), 'in', this.userAccess.storeList),
              );

              void getDocs(storesQuery).then(qs => {
                  const storesMap = {};
                  const storesOrder: string[] = qs.docs.map((doc: QueryDocumentSnapshot<unknown>) => {
                    const storeId = doc.id;
                    storesMap[storeId] = doc.data() as StoreInfo;

                    // TODO: TEMPORARY DEFAULT
                    void this.getStoreEditFlagConfig(storeId).then();

                    return storeId;
                  });
                  storesOrder.sort((a: string, b: string) => storesMap[a].name < storesMap[b].name ? -1 : 1);
                  this.storesMapObs.next({stores: storesMap, order: storesOrder});
                },
              );
            }
          }, error => {
            console.error(`\nERROR: \n${error}`);
          });

          const userDocSub = docData(doc(this.firestore, path_users_userId(userId))).pipe(
            filter((data: IUser): boolean => !!data),
          ).subscribe(async data => {
            const d: IUser = data;
            if (d) {
              this.userObjSubject.next({
                id: this.userId, userName: d?.hasOwnProperty('userName') ? d.userName : userId, pp: d.pp,
              });
            }
          }, e => {
            console.error(`user document fetch failed ${userId}`);
            throw e;
          });
          this.fireAuthService.setLogoutCallback('user-document', () => {
            userDocSub.unsubscribe();
          });

          // TODO: Should really update last seen according to this? Can I? DO IT NOW!
          const visited: string[] = [];

          const pageAlert = async (url: string): Promise<void> => {
            url = url.includes('/') ? url.substr(0, url.indexOf('/')) : url;
            visited.push(url);
            await getDoc(doc(this.firestore, path_users_userId_page_alerts_url(userId, url))).then(async d => {
              if (d.exists) {
                let data = d.data();
                await this.userObj.pipe(take(1)).toPromise().then((uo: IUser) => {
                  if (!data) {
                    data = {};
                  }

                  if (!data.cssClass) {
                    data.cssClass = [];
                  }

                  if (typeof data.cssClass === 'string') {
                    data.cssClass = [data.cssClass];
                  }

                  if (!data.cssClass.includes('page-alert-modal')) {
                    data.cssClass.push('page-alert-modal');
                  }
                  data.cssClass.push('custom-alert');

                  if (!data.backdropDismiss) {
                    data.backdropDismiss = true;
                  }
                });
              }
            });
          };

          // @ts-ignore
          router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
              const url = event.urlAfterRedirects.replace('/', '--');
              if (!visited.includes(url)) {
                void pageAlert(url);
              }
            }
          });

          setTimeout(() => {
            const url = this.router.url.replace('/', '--');
            void pageAlert(url);
          }, 1500);
        }
      } else if (userAccessSub) {
        await this.router.navigate(['login']).then();
        userAccessSub.unsubscribe();
      }
    }, error => {
      console.error('user timing error\n' + error);
    });
  }

  get userObj(): Observable<IUser> {
    return this.userObjSubject.asObservable();
  }

  get getUserId(): string {
    return this.userId;
  }

  get stores(): Observable<{ stores: Record<string, StoreInfo>; order: string[] }> {
    return this.storesMapObs.asObservable();
  }

  get colleagues(): Observable<{
    users: Record<string, Colleague>; storesUsers: Record<string, string[]>;
  }> {
    return this.colleaguesBS.asObservable();
  }


  //TODO check here and look at the service for images in community form update branch
  getImageUrl(path: string): Observable<string> {
    const storageRef = ref(this.storage, path);
    return from(getDownloadURL(storageRef));
  }

  * sliceArrayForFBQueries<T>(values: T[]): Generator<T[]> {
    const MAX_FB_ARRAY_QUERY_SIZE = 30;
    let idx = 0;

    while (idx < values.length) {
      yield values.slice(idx, idx + MAX_FB_ARRAY_QUERY_SIZE);
      idx += MAX_FB_ARRAY_QUERY_SIZE;
    }
  }


  pageStores(page: string): Observable<StoreObject> {
    const rules: false | (RuleHumanID | AccessFlags)[] = pageRules(page);
    if (!rules) {
      return EMPTY;
    }
    return this.stores.pipe(
      map((stores) => {
        const filtered: { order: string[]; stores: Record<string, StoreInfo> } = {order: [], stores: {}};

        // Empty rules = any authenticated store user (same as pageRules []).
        if ((rules as RuleHumanID[]).length === 0) {
          filtered.order = [...stores.order];
          filtered.stores = {...stores.stores};
          return filtered;
        }

        // OR across rules — match side-nav / validateAccess (e.g. stock-take i.|j.).
        filtered.order = stores.order.filter((storeId) => {
          const hasAnyRule: boolean = (rules as RuleHumanID[]).some(
            (rule: RuleHumanID) => this.fireAuthService.hasAccess(storeId, {ruleID: rule}) === true,
          );
          if (!hasAnyRule) {
            return false;
          }
          filtered.stores[storeId] = stores.stores[storeId];
          return true;
        });

        return filtered;
      }),
    );
  }

  async getUserPreferences<T>(document: string, defaultFactory?: () => T): Promise<T> {
    try {
      const docSnapshot = await getDoc(
        doc(this.firestore, path_users_userId_settings_saved_preferences_visible_Fields_feature(
          this.userId, document,
        )),
      );
      if (docSnapshot.exists()) {
        return docSnapshot.data() as T;
      } else if (defaultFactory) {
        return defaultFactory();
      }
      return undefined;
    } catch (e) {
      e.message = `Error fetching user Preference Document: ${document}\n${e.message}`;
      throw e;
    }
  }

  async updateUserPreferences<T = unknown>(
    feature: string,
    updates: T,
  ): Promise<void> {
    try {
      await setDoc(
        doc(this.firestore, path_users_userId_settings_saved_preferences_visible_Fields_feature(this.userId, feature)),
        updates,
        {merge: true},
      );
    } catch (e) {
      e.message = `Error updating user Preference Document: ${feature}\n${e.message}`;
      throw e;
    }
  }

  updateUserSingularDocuments<T = unknown>(document: string, updates: Record<string, T>): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const docRef = doc(this.firestore, path_users_userId_singular_documents_document(this.userId, document));
      updateDoc(docRef, updates)
        .then(() => {
          resolve();
        }).catch(_ => {
        //  TODO: Check the document isn't accidentally overwritten
        setDoc(docRef, updates)
          .then(() => {
            resolve();
          })
          .catch(e => {
            e.message = `Error updating user document: ${document}\n${e.message}`;
            reject(e);
          });
      });
    });
  }

  setUserDocument(userId: string, userDocument: IUser, merge: boolean = true): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      setDoc(doc(this.firestore, path_users_userId(userId)), userDocument, {merge})
        .then(() => {
          resolve();
        }).catch(e => {
        e.message = `Error setting user document: \n${e.message}`;
        reject(e);
      });
    });
  }

  /* ---------------------------------------------------------------------------------------------------------------- *\
  //                                               Adding Users                                                       //
  \* ---------------------------------------------------------------------------------------------------------------- */

  async getGroupAccessSettings(): Promise<IGroupAccess> {
    const result: IGroupAccess = {};

    try {
      const snapshot = await getDocs(collection(this.firestore, createGlobalPaths().groupAccess()));
      snapshot.forEach(doc => {
        const data = doc.data() as IDefaultGroupAccessDocument;
        if (data.groupName) {
          result[data.groupName] = {
            accessCode: data.accessCode,
            orderPriority: data.orderPriority,
          };
        }
      });
    } catch (error) {
      console.error('Error fetching group access settings:', error);
    }

    return result;
  }


  /* ---------------------------------------------------------------------------------------------------------------- *\
  //                                               Single Docs PATHS                                                  //
  \* ---------------------------------------------------------------------------------------------------------------- */

  async getPriceBandingForStores(): Promise<IPriceBandPerStore> {
    let results: IPriceBandPerStore = Object.create(null);
    if (!Object.isExtensible(results)) {
      results = {...results};
    }
    const storeIds: string[] = this.storesMapObs.value.order.filter((storeId: string) =>
      this.fireAuthService.hasAccess(storeId, {ruleID: 'b.i'}),
    );

    const documentPromises: Promise<void>[] = [];

    storeIds.forEach((storeId: string): void => {
      documentPromises.push(
        this.collectionStoresSettingsService.getDocument<IPriceBand>(path_stores_storeId_settings_price_bands(storeId))
          .then((data: Record<number, IPriceBand>): void => {
            if (!data) {
              void this.collectionGlobalService.getDocument(createGlobalPaths().priceBands())
                .then((globalData: Record<number, IPriceBand>): void => {
                  if (globalData) {
                    if (!Object.isExtensible(results)) {
                      results = {...results};
                    }
                    const globalPriceBandsCopy = getDeepCopyOfObject(globalData);
                    results[getDeepCopyOfObject(storeId)] = [];
                    Object.keys(globalPriceBandsCopy).forEach((index: string): void => {
                      results[storeId].push(getDeepCopyOfObject(globalPriceBandsCopy[index]) as IPriceBand);
                    });
                    void this.collectionStoresSettingsService.setDocument<Record<number, IPriceBand>>(
                      path_stores_storeId_settings_price_bands(storeId),
                      {...globalPriceBandsCopy},
                    );
                  }
                });
            } else {
              results[storeId] = [];
              Object.keys(data).forEach((index: string): void => {
                results[storeId].push(data[index] as IPriceBand);
              });
            }
          }),
      );
    });
    await Promise.all(documentPromises);
    return results;
  }

  // Old get price banding - deprecated
  async getPriceBands(): Promise<{
    stores: Record<string, PriceBand[]>
    personal?: PriceBand[];
  }> {
    const storeIds = this.storesMapObs.value.order;
    storeIds.filter((sID) => this.fireAuthService.hasAccess(sID, {ruleID: 'b.i'}));
    const result: { personal: PriceBand[]; stores: Record<string, PriceBand[]> } = {
      personal: null,
      stores: {},
    };
    const promises: Promise<void>[] = [];
    storeIds.forEach((sID) => promises.push(new Promise<void>((resolve, reject) =>
      this.collectionStoresSettingsService.getDocument<IPriceBand>(path_stores_storeId_settings_price_bands(sID)).then((data) => {
        if (data) {
          result.stores[sID] = [];
          for (let i = 0; i < Object.keys(data).length; i++) {
            result.stores[sID].push(data[i]);
          }
        } else {
          result.stores[sID] = null;
        }
        resolve();
      }),
    )));
    await forkJoin(promises).toPromise();
    return result;
  }

  getStoreDataDoc<T = unknown>(document: string, storeId: string, feature: Feature = 'shared'): Promise<Record<string, T>> {
    return new Promise<Record<string, T>>((resolve, reject) => {
      getDoc(doc(this.firestore, path_feature_stores_data_storeId_singular_documents_document(feature, storeId, document)))
        .then(docSnapshot => {
          resolve(docSnapshot.data());
        }).catch(e => {
        e.message = `Error getting store data doc: ${feature} -> ${document}\n${e.message}`;
        reject(e);
      });
    });
  }

  getStoreDataDocTyped<DocType>(document: string, store: IStore, feature: Feature = 'shared'): Promise<DocType> {
    return new Promise<DocType>((resolve, reject): void => {
      getDoc(doc(this.firestore, path_feature_stores_data_storeId_singular_documents_document(feature, store.storeId, document)))
        .then((docSnapshot: DocumentSnapshot<DocType>) => {
          resolve(docSnapshot.data());
        }).catch((error): void => {
        error.message = `Error getting store data doc: ${feature} -> ${document}\n${error.message}`;
        reject(error);
      });
    });
  }

  getDepartmentsByStore(store: IStore): Observable<IDepartment[]> {
    const departments$ = this.getStoreDataDocTyped<Record<string, IDepartment>>('departments', store);
    const subDepartments$ = this.getStoreDataDocTyped<Record<string, ISubDepartment>>('sub_departments', store);
    return forkJoin([departments$, subDepartments$]).pipe(
      map(([departments, subDepartments]) => {
        const deps: IDepartment[] = [];
        const subDepsAll: ISubDepartment[] = [];
        Object.keys(subDepartments).forEach((key: string): void => {
          subDepsAll.push({
            subDepartment: subDepartments[key].subDepartment,
            department: subDepartments[key].department,
            name: subDepartments[key].name,
            targetGP: subDepartments[key].targetGP,
          } as ISubDepartment);
        });
        Object.keys(departments).forEach((key: string) => {
          deps.push({
            department: key,
            name: departments[key].name,
            subDepartments: subDepsAll.filter((dep: ISubDepartment): boolean => dep.department === key),
          } as IDepartment);
        });
        return deps;
      }),
    );
  }

  async getStoreEditFlagConfig(storeId: string): Promise<StockValChangeCheckConfig> {
    const config = await this.collectionStoresSettingsService
      .getDocument<StockValChangeCheckConfig>(path_stores_storeId_settings_stock_edit_thresholds(storeId));
    if (config) {
      this.sVChangeCheckConfigs[storeId] = config;
    } else {
      this.sVChangeCheckConfigs[storeId] = {
        sellPriIncl1: {
          pct: 0.2, negPct: 0.1, noNeg: false, noZero: false,
        },
      };
    }
    return this.sVChangeCheckConfigs[storeId];
  }

  updateStoreDataDoc<T = unknown>(
    document: string,
    storeId: string,
    data: Record<string, T>,
    feature: Feature = 'shared',
    set: boolean = false,
    safeFieldPath: boolean = false,
  ): Promise<void> {
    if (document === 'price_bands') {
      return new Promise<void>((resolve, reject) => {
        this.collectionStoresSettingsService.setDocument<IPriceBand>(path_stores_storeId_settings_price_bands(storeId), data as IPriceBand)
          .then(() => {
            resolve();
          })
          .catch(e => {
            e.message = `Error getting price bands for store: ${storeId}\n${e.message}`;
            reject(e);
          });
      });
    } else if (document === 'stock_edit_thresholds') {
      return new Promise<void>((resolve, reject) => {
        this.collectionStoresSettingsService.setDocument<StockValChangeCheckConfig>(path_stores_storeId_settings_stock_edit_thresholds(storeId), data as StockValChangeCheckConfig)
          .then(() => {
            resolve();
          })
          .catch(e => {
            e.message = `Error getting price bands for store: ${storeId}\n${e.message}`;
            reject(e);
          });
      });
    } else {
      return new Promise<void>((resolve, reject) => {
        const path = path_feature_stores_data_storeId_singular_documents_document(feature, storeId, document);
        const docRef = doc(this.firestore, path);
        const update = [];

        if (safeFieldPath) {
          Object.keys(data).forEach(key => update.push(new FieldPath(key), data[key]));
        }

        if (set) {
          if (!safeFieldPath) {
            setDoc(docRef, data).then(() => {
              resolve();
            }).catch(e => {
              e.message = `Error setting store data doc: ${feature} -> ${document}\n${e.message}`;
              reject(e);
            });
          } else {
            // Handle FieldPath with setDoc by constructing update object
            const updateData = {};
            for (let i = 0; i < update.length; i += 2) {
              const fieldPath = update[i];
              updateData[fieldPath.toString()] = update[i + 1];
            }
            setDoc(docRef, updateData).then(() => {
              resolve();
            }).catch(e => {
              e.message = `Error setting store data doc (with FieldPath): ${feature} -> ${document}\n${e.message}`;
              reject(e);
            });
          }
        } else {
          let updatePromise: Promise<void>;
          if (!safeFieldPath) {
            updatePromise = updateDoc(docRef, data);
          } else {
            // Handle FieldPath with updateDoc by constructing update object
            const updateData = {};
            for (let i = 0; i < update.length; i += 2) {
              const fieldPath = update[i];
              updateData[fieldPath.toString()] = update[i + 1];
            }
            updatePromise = updateDoc(docRef, updateData);
          }

          updatePromise.then(() => {
            resolve();
          }).catch(e => {
            if (e.name === 'FirebaseError' && e.code === 'not-found') {
              let setPromise: Promise<void>;
              if (!safeFieldPath) {
                setPromise = setDoc(docRef, data);
              } else {
                const updateData = {};
                for (let i = 0; i < update.length; i += 2) {
                  const fieldPath = update[i];
                  const value = update[i + 1];
                  updateData[fieldPath.toString()] = value;
                }
                setPromise = setDoc(docRef, updateData);
              }
              setPromise.then(() => {
                resolve();
              }).catch(_ => {
                e.message = `Error updating store data doc after doc was not found ` +
                  `${safeFieldPath ? ' (with FieldPath)' : ''}: ${feature} -> ${document}\n${e.message}`;
                reject(e);
              });
            } else {
              e.message = `Error updating store data doc${safeFieldPath ? ' (with FieldPath)' : ''}: ` +
                `${feature} -> ${document}\n${e.message}`;
              reject(e);
            }
          });
        }
      });
    }

  }


  subStoreDoc(docPath: string, storeId: string, feature: Feature = 'shared'): Observable<unknown> {
    return docData(doc(this.firestore, `${feature}/stores_data/${storeId}/${docPath}`)).pipe(mergeMap((data) => [data]));
  }

  /* ---------------------------------------------------------------------------------------------------------------- *\
  //                                               SHARED PATHS                                                       //
  \* ---------------------------------------------------------------------------------------------------------------- */

  updateStockTags(storeId: string, stockIds: string | string[], tag: string, remove: boolean = false): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const update = {['_tags']: remove ? ARRAY_REMOVE(tag) : ARRAY_UNION(tag)};
      if (typeof stockIds === 'string') {
        const encodedStockIds = encodeStrict(stockIds);
        const path = `shared/stores_data/${storeId}/data/stock/${encodedStockIds}`;
        updateDoc(doc(this.firestore, path), update)
          .then(resolve)
          .catch(e => {
            e.message = `Failed to union tags ${tag} to code ${stockIds} on store ${storeId}.\n${e.message}`;
            reject(e);
          });
      } else {
        const promises: Promise<void>[] = [];

        while (stockIds.length > 0) {
          const batch = writeBatch(this.firestore);

          for (const code of stockIds.splice(Math.max(0, stockIds.length - 500), 500)) {
            const encodedStockIds = encodeStrict(code);
            batch.update(doc(this.firestore, `shared/stores_data/${storeId}/data/stock/${encodedStockIds}`), update);
          }
          promises.push(batch.commit());
        }

        if (promises.length === 1) {
          promises[0].then(() => {
            resolve();
          })
            .catch(e => {
              e.message = `Failed to union tags ${tag} to codes ${stockIds} on store ${storeId}.\n${e.message}`;
              reject(e);
            });
        } else {
          forkJoin(promises).toPromise().then(() => {
            resolve();
          })
            .catch(e => {
              e.message = `Failed to union tags ${tag} to codes ${stockIds} on store ${storeId}.\n${e.message}`;
              reject(e);
            });
        }
      }
    });
  }

  subSuppliersOnceOff(storeId: string): { 'obs': Observable<Record<string, ISupplier>>; 'sub': Subscription } {
    let sub: Subscription;

    // Wrap in a try-catch for debugging purposes
    try {
      const obs = new Observable<Record<string, Supplier>>(observer => {
        sub = collectionChanges(collection(this.firestore, `shared/stores_data/${storeId}/data/suppliers`))
          .subscribe(snaps => {
            const suppliers: Record<string, ISupplier> = {};

            // Ensure we process only valid snapshot documents
            for (const snap of snaps) {
              if (snap.doc.exists()) {
                suppliers[snap.doc.id] = snap.doc.data() as ISupplier;
              }
            }

            // Send data to the observer
            observer.next(suppliers);

          }, error => {
            console.error('Error fetching suppliers:', error);
            observer.error(error);
          });
      });

      return {obs, sub};
    } catch (error) {
      console.error('An error occurred while fetching suppliers:', error);
      return {obs: new Observable(), sub: new Subscription()}; // Return fallback if something fails
    }
  }

  getSuppliers(storeId: string): Promise<Record<string, Supplier>> {
    return new Promise<Record<string, Supplier>>((resolve, reject) =>
      getDocs(collection(this.firestore, `shared/stores_data/${storeId}/data/suppliers`)).then(d => {
        const suppliers = {};
        d.docs.forEach((value: QueryDocumentSnapshot<ISupplier>) => suppliers[decodeURIComponent(value.id)] = value.data());
        resolve(suppliers);
      }).catch(error => {
        error.message = `Failed to get suppliers ${storeId}.\n${error.message}`;
        reject(error);
      }),
    );
  }

  /* ---------------------------------------------------------------------------------------------------------------- *\
  //                                              OPERATIONAL PATHS                                                   //
  \* ---------------------------------------------------------------------------------------------------------------- */

  updateAutoOrderingSchedules(
    storeId: string,
    accounts: Record<string, Record<string, IAutoOrderAccountSettings>>,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const params: { docRef: DocumentReference; data: IAutoOrderAccountSettings }[] = [];

      for (const acc of Object.keys(accounts)) {
        for (const day of Object.keys(accounts[acc])) {
          let encodedKey: string;
          try {
            encodedKey = encodeStrict(decodeURIComponent(acc));
          } catch {
            encodedKey = encodeStrict(acc);
          }
          const docRef = doc(this.firestore, `/operational/stores_data/${storeId}/data/auto_ordering/schedule/${day}/${encodedKey}`);
          const data = accounts[acc][day];
          params.push({docRef, data});
        }
      }

      const upload = (): void => {
        if (params.length) {
          const batch = writeBatch(this.firestore);

          for (let i = 0; i < Math.min(500, params.length); i++) {
            const p = params.pop();

            if (p.data) {
              batch.set(p.docRef, p.data);
            } else {
              batch.delete(p.docRef);
            }
          }
          batch.commit().then(() => {
            upload();
          }).catch(error => {
            error.message = `Failed to update schedule.\n${error.message}`;
            reject(error);
          });
        } else {
          resolve();
        }
      };
      upload();
    });
  }

  async getAutoOrderingScheduleSubs(
    storeId: string,
  ): Promise<Record<string, Observable<Record<string, IAutoOrderAccountSettings>>>> {
    const subs: Record<string, Observable<Record<string, IAutoOrderAccountSettings>>> = {};
    const dayArr = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
    const path = `/operational/stores_data/${storeId}/data/auto_ordering/schedule`;

    // Always create a `collectionChanges()` subscription for every day, even if the
    // collection is currently empty. Each observable emits an empty record immediately
    // via `startWith({})` so downstream loading state can complete for stores that
    // have auto-order access but no schedule documents yet.
    for (const day of dayArr) {
      try {
        const dayRef = collection(this.firestore, `${path}/${day}`);

        subs[day] = collectionChanges(dayRef).pipe(
          map((dcs): Record<string, IAutoOrderAccountSettings> => {
            const accounts: Record<string, IAutoOrderAccountSettings> = {};

            for (const dc of dcs) {
              if (dc.doc.exists()) {
                accounts[decodeURIComponent(dc.doc.id)] =
                  dc.doc.data() as IAutoOrderAccountSettings;
              }
            }

            return accounts;
          }),
          startWith<Record<string, IAutoOrderAccountSettings>>({}),
        );
      } catch (error) {
        console.error(`Error setting up schedule subscription for day ${day}:`, error);
        // Fall back to an empty emission so the day still counts toward
        // `daysLoaded` and the loading orchestrator can complete.
        subs[day] = of<Record<string, IAutoOrderAccountSettings>>({});
      }
    }

    return subs;
  }

  getAutoOrderingScheduleDay(storeId: string, day: string): Promise<Record<string, IAutoOrderAccountSettings>> {
    return new Promise<Record<string, IAutoOrderAccountSettings>>((resolve, reject) => {
      getDocs(collection(this.firestore, `/operational/stores_data/${storeId}/data/auto_ordering/schedule/${day}`))
        .then(docs => {
          const orders = {};
          docs.docs.map(doc => orders[decodeURIComponent(doc.id)] = doc.data());
          resolve(orders);
        }).catch(e => {
        e.message = `Error getting AO Schedule for day ${day}.\n${e.message}`;
        reject(e);
      });
    });
  }

  subAutoOrders(storeId: string): Observable<TAutoOrderRecords> {
    return new Observable<TAutoOrderRecords>((subscriber: Subscriber<TAutoOrderRecords>) => {

      const process = async (docs): Promise<void> => {
        const aoCollection: TAutoOrderRecords = {};
        await Promise.all(
          docs.map(async (doc) => {
            const docData = doc.data();
            const autoOrder: AutoOrder = {
              ...docData,
              generated: docData.generated.toDate(),
              orderItems: {},
            };
            const orderItemsSnapshot = await getDocs(
              collection(this.firestore, `${this.autoOrderCollectionPath(storeId)}/${doc.id}/orderItems`),
            );
            if (!orderItemsSnapshot.empty) {
              orderItemsSnapshot.docs.forEach((itemDoc): void => {
                autoOrder.orderItems[itemDoc.id] = itemDoc.data() as IAutoOrderOrderItem;
              });
            } else if (docData.items) {
              autoOrder.orderItems = {...docData.items};
            }
            aoCollection[doc.id] = autoOrder;
          }),
        );
        subscriber.next(aoCollection);
      };

      // Legacy d.i (AoSeePeers) — still honoured for users with bit 6; not assignable via Customise Permissions UI.
      if (this.userAccess.stores[storeId] && this.fireAuthService.hasAccess(storeId, {ruleID: 'd.i'}) === true) {
        collectionChanges(collection(this.firestore, `${this.autoOrderCollectionPath(storeId)}/`))
          .subscribe(
            (docChanges) => {
              const addedDocs = docChanges.filter((change) => change.type === 'added' || change.type === 'modified');
              return process(addedDocs.map((d) => d.doc)); // Added closing parenthesis here
            },
            (error) => {
              subscriber.error(Error(`Error on subscription to ready auto orders (colleagues).\n${error}`));
            },
          );
      } else {
        const q = query(
          collection(this.firestore, `${this.autoOrderCollectionPath(storeId)}/`),
          where('userId', '==', this.userId),
        );
        onSnapshot(
          q,
          (qs) => {
            return process(qs.docs);
          },
          (error) => {
            subscriber.error(Error(`Error on subscription to ready auto orders.\n${error}`));
          },
        );
      }
    });
  }

  async addItemsToAutoOrder(storeId: string, orderId: string, codes: StockItem[], supplierId: string): Promise<void> {
    try {
      const batch = writeBatch(this.firestore);

      // Define the value to be added to each order item
      const value: IAutoOrderOrderItem = {
        mainSupplier: 1,
        outOfStock: 0,
        maxSold: 0,
        netSold: 0,
        qtyDiff: 0,
        maxReturned: 0,
        negativeDays: 0,
        zeroDays: 0,
        new: 'ADDED',
      };

      codes.forEach((code) => {
        const itemDocRef = doc(this.firestore, PATH_AUTO_ORDER_ORDER_ITEMS_BY_STORE_ID_AND_ORDER_ID_AT_ITEM_CODE(storeId, orderId, code.code));

        if (code.regularSuppCode === supplierId) {
          value.mainSupplier = 1;
        }

        batch.set(itemDocRef, value, {merge: true});
      });

      // Commit the batch operation
      await batch.commit();
    } catch (error) {
      console.error(`Error adding items to auto order '${orderId}': ${error.message}`);
    }
  }


  async setAOQuantities(
    storeId: string, order: AutoOrder, preparedItems: Record<string, IAutoOrderFirestoreOrderPreparedInfoDetails>, total: number,
    status: string, comment?: string, dirtyCodes?: string[],
  ): Promise<void> {
    // suppAccount: string, orderId: string,
    // quantities: { [code: string]: { qty: number; price?: number } }, dirtyCodes: string[],
    // ogUserID: string, total: number, comment?: string): Promise<void> {

    const payload: IAutoOrderFirestoreOrderPreparedInfo = {ts: SERVER_TIMESTAMP(), preparedItems, total};

    if (comment) {
      payload.comment = comment;
    } else if (order?.preparedInfo?.comment) {
      payload.comment = order.preparedInfo.comment;
    }

    try {
      await updateDoc(
        doc(this.firestore, `${this.autoOrderCollectionPath(storeId)}/${order.orderId}`),
        {userId: this.userId, status, preparedInfo: payload},
      );
    } catch (error) {
      throw Error(`Error in setAOQuantities. Changes not updated.\n${error}`);
    }
  }

  async deleteAutoOrder(storeId: string, orderId: string | string[]): Promise<void> {
    const toDelete = typeof orderId === 'string' ? [orderId] : orderId;

    try {
      for (const oID of toDelete) {
        const orderDocRef = doc(this.firestore, `${this.autoOrderCollectionPath(storeId)}/${oID}`);

        // Check if 'orderItems' sub collection exists and delete its documents
        const orderItemsSnapshot = await getDocs(
          collection(this.firestore, `${this.autoOrderCollectionPath(storeId)}/${oID}/orderItems`),
        );

        const batch = writeBatch(this.firestore);

        if (!orderItemsSnapshot.empty) {
          orderItemsSnapshot.forEach(docSnapshot => {
            batch.delete(docSnapshot.ref); // Delete each document in 'orderItems' subcollection
          });
        }

        // Delete the order document itself
        batch.delete(orderDocRef);

        // Commit the batch delete
        await batch.commit();
      }
    } catch (e) {
      e.message = `Error deleting Order: \n${e.message}`;
      throw e;
    }
  }

  async submitAutoOrder(
    storeId: string,
    orderIds: string[],
    recipients: { [orderId: string]: string[] },
    skipEmails: { [orderId: string]: boolean },
  ): Promise<void> {
    const batch = writeBatch(this.firestore);

    for (const orderId of orderIds) {
      const docPath = `${this.autoOrderCollectionPath(storeId)}/${orderId}`;
      batch.update(doc(this.firestore, docPath), {
        status: 'SUBMITTED',
        emailStatus: skipEmails[orderId] ? 'SKIP' : 'READY',
        recipients: recipients[orderId],
        userId: this.userId,
      });
    }
    const msgRef = doc(collection(this.firestore, path_operational_stores_data_storeId_messages_from_app(storeId)));
    const msg: Message<string[]> = {
      type: 'AUTO_ORDERS',
      payload: {
        data: orderIds,
      },
      sender: this.userId,
      timestamp: createOperationalServerTimestamp(),
    };
    batch.set(msgRef, msg);

    try {
      await batch.commit();
    } catch (e) {
      e.message = 'Submitting AO batch failed.\n' + e.message;
      throw e;
    }
  }

  requestAutoOrderUnscheduled(storeId: string, orderData: Record<string, IAutoOrderAccountSettings>): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      const normalizedOrderData = normalizeAutoOrderUnscheduledPayload(orderData);
      const msg: Message<Record<string, IAutoOrderAccountSettings>> = {
        type: 'AUTO_ORDERS_U_UPDATE',
        timestamp: createOperationalServerTimestamp(),
        sender: this.userId,
        payload: {data: normalizedOrderData},
      };
      const undefinedSuppliers = [];
      Object.keys(orderData).forEach((supplier: string) => {
        if (supplier == null) {
          undefinedSuppliers.push(supplier);
        }
      });

      if (undefinedSuppliers.length > 0) {
        const componentProps: ISharedModalBasic = {
          buttonAccept: false,
          buttonAcceptText: '',
          buttonClose: true,
          buttonCloseCross: false,
          buttonCloseText: `Cancel Order`,
          modalTitle: 'An error has occurred while generating this order',
          modalTitleIcon: IconsEnum.infoSlabCircleOutline,
          modalTitleIconColor: IconColourEnum.red,
          contextHeading: 'Please contact Gallix support',
          contextDescription: [
            `Please let us know which suppliers you were trying to place an order for `,
          ],
        };
        const modalController = await this.modalController.create({
          backdropDismiss: false,
          component: SharedModalBasicComponent,
          componentProps,
          cssClass: ['shared-basic-modal-css-small'],
        });
        await modalController.present();
        const modalResponse: IModalDismissResult<ISharedModalBasicResponse> = await modalController.onDidDismiss();
        return;
      }
      const msgRef = doc(collection(this.firestore, path_operational_stores_data_storeId_messages_from_app(storeId)));
      setDoc(msgRef, msg)
        .then(() => {

          resolve();
        })
        .catch(e => {
          e.message = `Error sending unscheduled order to server ${storeId}. ${e.message}`;
          reject(e);
        });
    });
  }

  /**
   * Sends barcode-scanner created auto orders grouped by supplier.
   */
  requestAutoOrderUnscheduledFromScanner(
    storeId: string,
    orderData: Record<string, IScannerAutoOrderRequest>,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const normalizedOrderData: Record<string, IScannerAutoOrderRequest> = Object.fromEntries(
        Object.entries(orderData).map(([supplierId, settings]: [string, IScannerAutoOrderRequest]) => {
          const normalizedSettings = normalizeAutoOrderUnscheduledPayload({
            [supplierId]: settings,
          })[supplierId];
          const supplierPayload: IScannerAutoOrderRequest = {
            growth: Number(normalizedSettings.growth),
            monthsHistory: Number(normalizedSettings.monthsHistory),
            period: Number(normalizedSettings.period),
            preparedItems: settings.preparedItems ?? {},
            stockDays: Number(normalizedSettings.stockDays),
            total: Number(settings.total ?? 0),
            userId: settings.userId,
            weeksHistory: Number(settings.weeksHistory ?? 0),
          };
          if (settings.comment) {
            supplierPayload.comment = settings.comment;
          }
          return [supplierId, supplierPayload];
        }),
      );

      const msg: Message<Record<string, IScannerAutoOrderRequest>> = {
        type: 'AUTO_ORDERS_U_UPDATE',
        timestamp: createOperationalServerTimestamp(),
        sender: this.userId,
        payload: {data: normalizedOrderData},
      };
      const msgRef = doc(collection(this.firestore, path_operational_stores_data_storeId_messages_from_app(storeId)));
      setDoc(msgRef, msg)
        .then(() => {
          resolve();
        })
        .catch((e) => {
          e.message = `Error sending scanner order request to server ${storeId}. ${e.message}`;
          reject(e);
        });
    });
  }

  autoOrderCollectionPath(storeId: string): string {
    return path_operational_stores_data_auto_ordering_auto_orders_GET(storeId);
  }


  /* ________________________________________________ STOCK UPDATES _________________________________________________ */

  stockUpdate(
    storeId: string,
    updates: Record<string, StockItemUpdateObj>,
    considerThreshHolds: boolean = true,
    newStockItem: boolean = false,
    scheduledDate?: Date,
  ): Promise<string> {
    return new Promise<string>(async (resolve, reject) => {
      const invalidPackedFields = this.findInvalidPackedStockUpdateFields(updates, newStockItem);
      if (invalidPackedFields.length > 0) {
        const itemCount = Object.keys(updates).length;
        reject({
          message: this.buildInvalidPackedFieldMessage(itemCount, invalidPackedFields),
          name: 'StockUpdateValidationError',
        });
        return;
      }

      const data: Record<string, StockItemUpdateObj> = {};

      const newItems: Record<string, StockItem> = {};
      const oldItems: Record<string, StockItem> = {};
      Object.keys(updates).forEach(code => {
        data[code] = {o: {}, n: {}, details: {}};
        Object.keys(updates[code].n).forEach((k) => {
          const key = k as keyof StockItem;
          const hasOriginalValue = Object.prototype.hasOwnProperty.call(updates[code].o, k);
          const newValue = updates[code].n[k];
          const oldValue = updates[code].o[k];
          const valuesDiffer =
            newValue !== oldValue ||
            JSON.stringify(newValue) !== JSON.stringify(oldValue);
          const shouldIncludeField =
            (newStockItem || editableKeys.includes(k)) &&
            (!hasOriginalValue || valuesDiffer);

          if (!shouldIncludeField) {
            return;
          }

          data[code].n[sItemKeyToInt[k]] = newValue;
          data[code].o[sItemKeyToInt[k]] = oldValue;
          data[code].details = updates[code].details;

          if (!newItems[code]) {
            newItems[code] = {} as StockItem;
            oldItems[code] = {} as StockItem;
          }
          newItems[code][k] = newValue;
          oldItems[code][k] = oldValue;
        });
      });

      const changeChecks = this.sVChangeCheckConfigs[storeId];
      if (changeChecks) {
        const itemFlags: Record<string, StockValuesChangeCheckResult> = {};
        const flaggedCodes = Object.keys(newItems).filter((code) => {
          const flags = StockFunctions.checkStockValues(newItems[code], this.sVChangeCheckConfigs[storeId],
            oldItems[code]);
          if (flags) {
            itemFlags[code] = flags;
            return true;
          }
          return false;
        });

        if (flaggedCodes.length && considerThreshHolds) {
          const ac = await this.modalController.create({
            component: StockValChangeFlagsComponent,
            componentProps: {codes: flaggedCodes, itemFlags},
            cssClass: ['stock-threshold-modal'],
            backdropDismiss: false,
          });
          await ac.present();
          const result = await ac.onDidDismiss();
          if (result.role === 'submit') {
            for (const code of Object.keys(result.data)) {
              for (const key of Object.keys(result.data[code])) {
                newItems[code][key] = result.data[code][key];
                data[code].n[sItemKeyToInt[key]] = result.data[code][key];
              }
            }
          } else if (result.role === 'cancel') {
            const error: IGlobalError = {
              message: 'Update Cancelled',
              name: 'User Error',
              storeIds: [storeId],
            };
            reject({error, flaggedCodes});
            return;
          }
        }
      }

      const apiLogObj: ApiLogObj2 = {
        creationDate: createOperationalServerTimestamp(),
        data,
        type: 'STOCK_UPDATE',
        userId: this.userId,
        editedItems: Object.keys(data),
      };
      const msg: Message<{ [key: string]: string | boolean | Date }> = {
        sender: this.userId,
        timestamp: createOperationalServerTimestamp(),
        type: 'STOCK_UPDATE',
        payload: {
          data: {},
        },
      };

      if (scheduledDate && scheduledDate > new Date()) {
        msg.payload.data.scheduledDate = scheduledDate;
      }
      if (newStockItem) {
        apiLogObj.newStockItem = true;
        msg.newStockItem = true;
      }
      runTransaction(this.firestore, async trans => {
        const docRef = doc(collection(this.firestore, `/operational/stores_data/${storeId}/data/api_events/`));
        const msgRef = doc(collection(this.firestore, `/operational/stores_data/${storeId}/messages/from_app/`));
        msg.payload.data.logID = docRef.id;
        trans.set(docRef, apiLogObj);
        trans.set(msgRef, msg);
        return docRef.id;
      }).then((docID: string): void => {
        resolve(docID);
      })
        .catch(error => {
          error.message = 'Failed to run stock update transaction\n' + error.message;
          console.error(error.message);
          reject(error);
        });

    });
  }

  /**
   * Returns every stock code whose update would write an unmapped or unset packed field.
   * Firestore rejects the whole stock-update document when any packed value is unset.
   */
  private findInvalidPackedStockUpdateFields(
    updates: Record<string, StockItemUpdateObj>,
    newStockItem: boolean,
  ): {code: string; field: string}[] {
    const invalidFields: {code: string; field: string}[] = [];
    for (const code of Object.keys(updates)) {
      const nextFields = updates[code]?.n ?? {};
      for (const field of Object.keys(nextFields)) {
        const hasOriginalValue = Object.prototype.hasOwnProperty.call(updates[code].o ?? {}, field);
        const newValue = nextFields[field];
        const oldValue = updates[code].o?.[field];
        const valuesDiffer = newValue !== oldValue || JSON.stringify(newValue) !== JSON.stringify(oldValue);
        const shouldIncludeField = (newStockItem || editableKeys.includes(field)) && (!hasOriginalValue || valuesDiffer);
        if (!shouldIncludeField) {
          continue;
        }
        const packedKey = (sItemKeyToInt as Record<string, string | undefined>)[field];
        if (packedKey == null || packedKey === '' || this.isUnsetPackedValue(newValue)) {
          invalidFields.push({code, field});
        }
      }
    }
    return invalidFields;
  }

  private buildInvalidPackedFieldMessage(
    itemCount: number,
    invalidFields: {code: string; field: string}[],
  ): string {
    const labelsByCode = new Map<string, string[]>();
    invalidFields.forEach(({code, field}) => {
      const label = this.packedFieldLabel(field);
      const labels = labelsByCode.get(code) ?? [];
      if (!labels.includes(label)) {
        labels.push(label);
      }
      labelsByCode.set(code, labels);
    });
    const heldBack = [
      'Stock update was not sent.',
      `None of the ${itemCount} item${itemCount === 1 ? '' : 's'} were written.`,
    ];
    const details = [...labelsByCode.entries()].map(([code, labels]) => {
      const fieldList = labels.map((label: string) => `"${label}"`).join(', ');
      const fieldWord = labels.length === 1 ? 'field' : 'fields';
      return `Stock code ${code} has an invalid or unset ${fieldWord} ${fieldList}.`;
    });
    return [...heldBack, ...details].join('\n');
  }

  private packedFieldLabel(field: string): string {
    if (field === 'sellPriIncl1' || /^sellPriceIncluding\d+$/.test(field)) {
      return 'Retail Price';
    }
    return keyToTitle[field] ?? field;
  }

  private isUnsetPackedValue(value: unknown): boolean {
    if (value === undefined || (typeof value === 'number' && Number.isNaN(value))) {
      return true;
    }
    return Array.isArray(value) && value.some((entry: unknown) => this.isUnsetPackedValue(entry));
  }

}
